import React, { useState } from 'react'
import ReactPaginate, { ReactPaginateProps } from 'react-paginate'
import styled, { css } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons'
import { appTheme } from '../../../config/styled'

export type PaginationProps = {
  handlePageChange: (data: { selected: number }) => void,
} & ReactPaginateProps

const ReactPaginateStyle = styled.div<{ isMobile: boolean }>`
  ul {
    display: flex;
    border-radius: 0.25rem;
    list-style: none;
    padding-left: 0;
    margin-bottom: 8px;

    li {
      background-color: #fff;
      border: 1px solid ${props => props.theme.members.colors.silver._50};
      color: ${props => props.theme.members.colors.appleGreen._90};
      font-size: .875rem;
      font-weight: 700;
      line-height: 59px;
      text-align: center;
      margin: 0 -1px;
      padding: 0;
      width: 60px;
      height: 60px;

      a {
        display: block;
        background: none;
        border: none;
        color: ${props => props.theme.members.colors.appleGreen._90};
        font-size: .875rem;
        font-family: Open Sans,sans-serif;
        font-weight: 700;
        text-decoration: none;
        width: 60px;
        height: 60px;
        margin: 0;
        padding: 0;

        ${({ isMobile }) => !isMobile && css`
          font-weight: 600
        `}
      }
    }

    .selected {
      border-color: #0bbe78;
      background-color: #0bbe78;
      color: white;
    }

    .previous {
      text-align: center;
      width: calc(50vw - 46px);

      a {
        display: inline-block;
      }

      &.disable-label > a {
        color: ${props => props.theme.members.colors.silver._70};
      }
    }

    .next {
      text-align: center;
      width: calc(50vw - 46px);

      a {
        display: inline-block;
      }

      &.disable-label > a {
        color: ${props => props.theme.members.colors.silver._70};
      }
    }

    @media ${props => props.theme.media.sm} {
      .disable-label {
        display: none;
      }

      .break {
        color: #a6aab6;
      }

      .previous {
        width: auto;

        a {
          display: block;
        }
      }

      .next {
        width: auto;
 
        a {
          display: block;
        }
      }

      li {
        ${props => props.theme.members.colors.appleGreen._90};
        line-height: 50px;
        min-width: 52px;
        width: auto;
        height: 52px;

        a {
          width: 52px;
          height: 52px;
        }
      }
    }
  }
`

const CountPerPages = styled.div`
  color: ${props => props.theme.members.colors.silver._100};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 23.8px;
  text-align: center;
`

const StyledArrowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`

export const StyledPagination: React.FC<PaginationProps> = (props: PaginationProps) => {
  const [currentPage, setCurrentPage] = useState(props.initialPage || 1)
  const isMobile = navigator.userAgent.match(/iPhone|Android.+Mobile|iPad/)
  const pageRangeDisplayed = isMobile ? 0 : props.pageRangeDisplayed
  const marginPagesDisplayed = isMobile ? 0 : props.marginPagesDisplayed
  const previousLabel = () => {
    if (isMobile) {
      return (
        <>
          <StyledArrowLabel><FontAwesomeIcon icon={faAngleLeft} fontSize={16} /><span> 前へ</span></StyledArrowLabel>
        </>
      )
    }

    return <FontAwesomeIcon icon={faAngleLeft} />
  }
  const nextLabel = () => {
    if (isMobile) {
      return (
        <>
          <StyledArrowLabel data-cy="job-offers-sp-pagination-next"><span>次へ </span><FontAwesomeIcon icon={faAngleRight} fontSize={16} /></StyledArrowLabel>
        </>
      )
    }

    return <FontAwesomeIcon data-cy="job-offers-pc-pagination-next" icon={faAngleRight} />
  }
  const breakLabel = isMobile ? '' : '...'

  const handleClick = (data: { nextSelectedPage: number, isPrevious: boolean, isNext: boolean }) => {
    const pageNumber: number =
      data.nextSelectedPage !== undefined ? data.nextSelectedPage + 1 :
        data.isPrevious ? currentPage - 1 :
          data.isNext ? currentPage + 1 : currentPage
    setCurrentPage(pageNumber)
    props.handlePageChange({ selected: pageNumber })
  }

  return (
    <ReactPaginateStyle theme={appTheme} isMobile={isMobile}>
      <ReactPaginate
        onClick={handleClick}
        pageRangeDisplayed={pageRangeDisplayed}
        marginPagesDisplayed={marginPagesDisplayed}
        pageCount={Math.ceil(props.pageCount)}
        previousLabel={previousLabel()}
        nextLabel={nextLabel()}
        breakLabel={breakLabel}
        disabledClassName='disable-label'
        initialPage={currentPage - 1}
      />
      {isMobile &&
        <CountPerPages theme={appTheme}>
          {currentPage} / {props.pageCount}ページ
        </CountPerPages>
      }
    </ReactPaginateStyle>
  )
}
